import React from 'react';
import { useAsk } from '../../hooks/useAsk';
import { copyText } from '../../lib/clipboard';
import { detectPii, piiWarning } from '../../lib/piiCheck';
import { loadConversation, saveConversation, clearConversation } from '../../lib/conversationStore';
import { renderAnswer, SourcesBlock } from './answerParts';
import { TransitCard } from './TransitCard';
import { plainAnswer } from '../../lib/streamParse.mjs';

/**
 * ChatThread — multi-turn conversation over /api/ask (Widget v2, plan W2).
 *
 * Owns the message list; each completed answer freezes into `messages` with its
 * own sources/citations/requestId so votes and copies target the right answer
 * even after later turns. History (last 6 frozen turns) rides along on every
 * submit — the server condenses it into a standalone retrieval query, which is
 * what makes follow-ups like "how do I apply for it?" resolve.
 *
 * Accessibility: thread is role="log" aria-live="polite"; every control is a
 * real button with a label; Esc clears the input only (the thread persists);
 * auto-scroll sticks to the bottom only when the reader is already there.
 */

const MAX_HISTORY_TURNS = 6;

let nextId = 1;
// Random suffix so ids minted this session never collide with ids restored
// from a previous session's saved transcript (the counter resets on reload).
const newId = () => `m${nextId++}_${Math.random().toString(36).slice(2, 6)}`;

export const ChatThread = React.forwardRef(function ChatThread({
  backendUrl, siteId = '', signedToken = '', channelUrl = '', customerKey = '',
  agencyName = 'this agency', tracker = null, c,
  starterQuestions = [], inputPlaceholder = '', answerDisclaimer = '',
  sourcesDisplay = 'collapsed',
  audiences = null, audienceGate = false, audiencePrompt = 'Which best describes you?',
  onAudienceChange = null,
  onStarterClick = null, disabled = false, trackOpenOnFocus = true,
}, ref) {
  // 24-hour conversation memory: restore any saved transcript for this site
  // once, up front, so the thread and the conversation id both continue.
  const scope = siteId || channelUrl || 'default';
  const restoredRef = React.useRef(null);
  if (restoredRef.current === null) {
    restoredRef.current = loadConversation(scope) || { messages: [], conversationId: null };
  }
  const convoIdRef = React.useRef(
    restoredRef.current.conversationId
    || ('c-' + Math.random().toString(36).slice(2) + Date.now().toString(36)));

  const [audience, setAudience] = React.useState(restoredRef.current.audience || null);
  const [requiredAudiences, setRequiredAudiences] = React.useState(null);
  const [pendingQuestion, setPendingQuestion] = React.useState(null);
  const vocabulary = requiredAudiences || audiences || [];
  const selectedAudience = vocabulary.find((a) => a.id === audience);
  const gated = (audienceGate || requiredAudiences !== null) && vocabulary.length > 0 && !selectedAudience;
  const starters = selectedAudience?.starter_questions?.length
    ? selectedAudience.starter_questions : starterQuestions;

  const ask = useAsk({ backendUrl, siteId, signedToken, channelUrl, customerKey,
                       agencyName, conversationId: convoIdRef.current, audience });
  const [messages, setMessages] = React.useState(restoredRef.current.messages);
  const [input, setInput] = React.useState('');
  const [piiNotice, setPiiNotice] = React.useState(null);
  const [copiedId, setCopiedId] = React.useState(null);
  const scrollRef = React.useRef(null);
  const stickRef = React.useRef(true);
  const openedRef = React.useRef(false);
  const inputRef = React.useRef(null);
  const askRef = React.useRef(ask);
  askRef.current = ask;
  const messagesRef = React.useRef(messages);
  messagesRef.current = messages;

  const streaming = ask.status === 'streaming';
  const track = React.useCallback((type, props) => {
    if (!tracker) return;
    if (props === undefined) tracker.track(type);
    else tracker.track(type, props);
  }, [tracker]);

  // Freeze the in-flight answer into the thread when it completes or errors.
  React.useEffect(() => {
    if (ask.status !== 'done' && ask.status !== 'error') return;
    const a = askRef.current;
    if (a.errorDetails?.code === 'audience_required') {
      // The gate went live mid-conversation. Keep the visitor's transcript and
      // their unanswered question — show the picker, then send it for them.
      setRequiredAudiences(a.errorDetails.audiences);
      setPendingQuestion([...messagesRef.current].reverse().find((m) => m.role === 'user')?.text || null);
      a.reset();
      return;
    }
    setMessages((msgs) => [...msgs, {
      id: newId(), role: 'bot',
      text: a.answer,
      sources: a.sources, used: a.used,
      itinerary: a.itinerary || null, lang: (a.done && a.done.lang) || null,
      requestId: a.requestId,
      refused: !!(a.done && a.done.refused),
      error: a.status === 'error' ? (a.error || 'error') : null,
      vote: null,
    }]);
    track('answer_completed', {
      refused: !!(a.done && a.done.refused),
      ms: a.done && a.done.timings ? a.done.timings.total_ms : undefined,
    });
    if (a.status === 'error') track('error_shown');
    a.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask.status]);

  // Stick-to-bottom auto-scroll: only when the reader is already at the bottom.
  const onScroll = () => {
    const el = scrollRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, ask.answer]);

  // Persist the transcript (24h memory). Runs on every message change; storing
  // an empty thread clears the entry. conversation_id rides along so a resumed
  // chat keeps its grouping.
  React.useEffect(() => {
    saveConversation(scope, messages, convoIdRef.current, audience);
  }, [messages, scope, audience]);

  const clearConversationThread = React.useCallback(() => {
    askRef.current.reset();
    setMessages([]);
    setAudience(null);
    setInput('');
    setPiiNotice(null);
    clearConversation(scope);
    // fresh conversation id so the next question starts a new group
    convoIdRef.current = 'c-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    inputRef.current?.focus();
  }, [scope]);

  const changeAudience = React.useCallback(() => {
    if (!window.confirm('Change audience? This clears your conversation and starts a new one.')) return;
    track('audience_changed', { id: audience });
    clearConversationThread();
  }, [audience, track, clearConversationThread]);
  React.useImperativeHandle(ref, () => ({ changeAudience }), [changeAudience]);
  React.useEffect(() => {
    if (onAudienceChange) onAudienceChange(selectedAudience || null);
  }, [selectedAudience, onAudienceChange]);
  React.useEffect(() => {
    if (audiences !== null && audience && !selectedAudience) setAudience(null);
  }, [audiences, audience, selectedAudience]);

  const pickAudience = (id) => {
    setAudience(id);
    track('audience_selected', { id });
  };


  const history = React.useCallback(() => messages
    .filter((m) => !m.error)
    .slice(-MAX_HISTORY_TURNS)
    .map((m) => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.text })),
  [messages]);
  // Resend once the picked audience has landed in state, so the retry carries
  // it. The question is already in the thread; drop it from the history copy
  // rather than sending it twice.
  React.useEffect(() => {
    if (!pendingQuestion || !selectedAudience || streaming) return;
    const turns = history();
    if (turns[turns.length - 1]?.content === pendingQuestion) turns.pop();
    setPendingQuestion(null);
    askRef.current.submit(pendingQuestion, turns);
  }, [pendingQuestion, selectedAudience, streaming, history]);

  const submitQuestion = (q) => {
    const question = (q || '').trim();
    if (!question || streaming || disabled || gated) return;
    // Pre-submit privacy guard: if the question carries obvious personal
    // information, coach the visitor to remove it instead of sending. The
    // server still redacts everything it stores — this just spares the visitor
    // from transmitting it at all. Logged (no content) so agencies can see how
    // often it fires.
    // The assistant's replies print the agency's own phone and email (its
    // closing line); repeating those back is not personal information.
    const pii = detectPii(question, messages.filter((m) => m.role === 'bot').map((m) => m.text).join('\n'));
    if (pii) {
      setPiiNotice(piiWarning(pii));
      track('pii_blocked');
      inputRef.current?.focus();
      return;
    }
    setPiiNotice(null);
    track('question_submitted');
    setMessages((msgs) => [...msgs, { id: newId(), role: 'user', text: question }]);
    ask.submit(question, history());
    setInput('');
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submitQuestion(input);
  };

  const onStarter = (q, i) => {
    track('starter_clicked', { n: i + 1 });
    if (onStarterClick) onStarterClick(q);
    submitQuestion(q);
  };

  // In float mode the launcher owns the chat_opened signal; the inline card
  // keeps the first-focus heuristic. Never both (modes are exclusive).
  const onFirstFocus = () => {
    if (!trackOpenOnFocus) return;
    if (!openedRef.current) { openedRef.current = true; track('chat_opened'); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setInput(''); inputRef.current?.focus(); }
  };

  const vote = (msg, v) => {
    if (msg.vote || !msg.requestId) return;
    setMessages((msgs) => msgs.map((m) => (m.id === msg.id ? { ...m, vote: v } : m)));
    ask.sendFeedback(v, '', msg.requestId);
  };

  const copy = async (msg) => {
    // link names stay, [n] citation markers go — they're link chrome
    const plain = plainAnswer(msg.text);
    if (await copyText(plain)) {
      setCopiedId(msg.id);
      track('answer_copied');
      setTimeout(() => setCopiedId((id) => (id === msg.id ? null : id)), 2000);
    }
  };

  const sourceByN = (sources) => {
    const m = {};
    (sources || []).forEach((s) => { m[s.n] = s; });
    return m;
  };
  const onCite = (s) => track('citation_clicked', { n: s.n, source_type: s.source_type });

  const bubbleBase = {
    maxWidth: '85%', padding: '9px 12px', borderRadius: 12, fontSize: 15,
    lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  };
  const showStarters = !gated && messages.length === 0 && !streaming && starters.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }} onKeyDown={onKeyDown}>
      {/* thread */}
      <div
        ref={scrollRef} onScroll={onScroll}
        role="log" aria-live="polite" aria-busy={streaming} aria-label="Conversation"
        style={{ flex: 1, minHeight: 120, overflowY: 'auto', padding: '4px 2px' }}
      >
        {gated && (
          <div style={{ padding: '12px 2px' }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{audiencePrompt}</p>
            {requiredAudiences !== null && messages.length > 0 && (
              <p style={{ fontSize: 13, color: c.muted, margin: '0 0 10px' }}>
                Your conversation is saved.{pendingQuestion ? ' Choose an option and I\u2019ll answer your last question.' : ''}
              </p>
            )}
            {vocabulary.map((a) => (
              <button key={a.id} type="button" className="inquiros-starter"
                onClick={() => pickAudience(a.id)} disabled={disabled}
                style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{a.label}</span>
                {a.description && <span style={{ display: 'block', fontSize: 12 }}>{a.description}</span>}
              </button>
            ))}
          </div>
        )}
        {showStarters && (
          <div style={{ margin: '4px 0 10px' }}>
            <div style={{ fontSize: 13, color: c.muted, marginBottom: 8 }}>
              Try one of these to get started:
            </div>
            {starters.map((q, i) => (
              <button key={i} type="button" className="inquiros-starter" onClick={() => onStarter(q, i)}>
                {q}
              </button>
            ))}
          </div>
        )}

        {messages.map((m) => m.role === 'user' ? (
          <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0' }}>
            <div style={{ ...bubbleBase, background: c.userBubble || c.accent, color: c.userText }}>{m.text}</div>
          </div>
        ) : (
          <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-start', margin: '8px 0' }}>
            <div style={{ ...bubbleBase, background: c.botBubble, color: c.text, maxWidth: '92%' }}>
              {m.error ? (
                <span role="alert" style={{ color: c.error }}>
                  {m.error === 'site_not_enabled'
                    ? "This assistant isn't enabled on this website yet."
                    : 'Something went wrong. Please try again.'}
                </span>
              ) : (
                <>
                  {renderAnswer(m.text, sourceByN(m.sources), onCite)}
                  {m.itinerary && !m.refused && <TransitCard itinerary={m.itinerary} c={c} lang={m.lang || 'en'} />}
                  {!m.refused && (
                    <SourcesBlock sources={m.sources} used={m.used} onCite={onCite}
                                  mutedColor={c.muted} textColor={c.text}
                                  defaultOpen={sourcesDisplay !== 'collapsed'}
                                  citedOnly={sourcesDisplay === 'cited'} />
                  )}
                  {answerDisclaimer && (
                    <div style={{ marginTop: 8, fontSize: 11.5, color: c.muted }}>{answerDisclaimer}</div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {m.requestId && (
                      <>
                        <button type="button" onClick={() => vote(m, 'up')} disabled={!!m.vote}
                          aria-label="Helpful" className="inquiros-vote"
                          style={{ opacity: m.vote && m.vote !== 'up' ? 0.4 : 1 }}>👍</button>
                        <button type="button" onClick={() => vote(m, 'down')} disabled={!!m.vote}
                          aria-label="Not helpful" className="inquiros-vote"
                          style={{ opacity: m.vote && m.vote !== 'down' ? 0.4 : 1 }}>👎</button>
                      </>
                    )}
                    <button type="button" onClick={() => copy(m)} aria-label="Copy answer"
                      className="inquiros-iconbtn">
                      {copiedId === m.id ? 'Copied' : 'Copy'}
                    </button>
                    {m.vote && <span style={{ fontSize: 12, color: c.muted }} role="status">Thanks for the feedback.</span>}
                    {copiedId === m.id && <span className="sr-only" role="status">Answer copied</span>}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        {/* in-flight answer */}
        {streaming && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '8px 0' }}>
            <div style={{ ...bubbleBase, background: c.botBubble, color: c.text, maxWidth: '92%' }}>
              {renderAnswer(ask.answer, sourceByN(ask.sources), onCite, { streaming: true })}
              <span className="inquiros-caret" aria-hidden="true">▍</span>
              {ask.itinerary && <TransitCard itinerary={ask.itinerary} c={c} />}
            </div>
          </div>
        )}
      </div>

      {/* screen-reader status */}
      <div aria-live="assertive" className="sr-only">
        {streaming ? 'Generating answer' : ''}
      </div>

      {/* clear the 24h-remembered conversation */}
      {messages.length > 0 && !streaming && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 6 }}>
          <button
            type="button" onClick={clearConversationThread}
            style={{
              background: 'none', border: 'none', padding: '2px 4px', fontSize: 12,
              color: c.muted, cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            Clear conversation
          </button>
        </div>
      )}

      {/* pre-submit privacy notice (personal info detected in the box) */}
      {piiNotice && (
        <div role="alert" style={{
          margin: '8px 0 0', padding: '8px 12px', fontSize: 13, lineHeight: 1.4,
          borderRadius: 8, border: `1px solid ${c.border}`,
          background: c.inputBg, color: c.text,
        }}>
          {piiNotice}
        </div>
      )}

      {/* composer */}
      <form onSubmit={onSubmit} role="search" aria-label={`Ask ${agencyName}`}
            style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: `1px solid ${c.border}` }}>
        <label htmlFor="inquiros-q" className="sr-only">Ask a question</label>
        <input
          id="inquiros-q" ref={inputRef} type="text" value={input}
          onChange={(e) => { setInput(e.target.value); if (piiNotice) setPiiNotice(null); }}
          disabled={streaming || disabled || gated}
          onFocus={onFirstFocus}
          maxLength={500} autoComplete="off"
          placeholder={gated ? 'Choose an option above to start' : inputPlaceholder || 'Ask a question…'}
          style={{
            flex: 1, padding: '10px 12px', fontSize: 15, borderRadius: 8,
            border: `1px solid ${c.border}`, background: c.inputBg, color: c.text,
          }}
        />
        <button
          type="submit" disabled={streaming || disabled || gated || !input.trim()}
          style={{
            padding: '10px 16px', fontSize: 15, fontWeight: 600, borderRadius: 8, border: 'none',
            background: streaming ? c.muted : c.accent, color: c.userText,
            cursor: streaming ? 'default' : 'pointer',
          }}
        >
          {streaming ? 'Asking…' : 'Ask'}
        </button>
      </form>
    </div>
  );
});

export default ChatThread;
